import { SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { createBrowserHistory } from 'history';
import React, { lazy } from 'react';
import { Route, Switch } from 'react-router-dom';
import AppErrorBoundary from 'src/components/App/AppErrorBoundary';
import DomainAuthenticatedRoutes from 'src/components/Authentication/DomainAuthenticatedRoutes';
import ContentLoader from 'src/components/ContentLoader/ContentLoader';
import { PAGE_PATHS } from 'src/constants/Urls';
import QueueViewWithUsersManagement from 'src/pages/CaseQueues/QueueViewWithUsersManagement';
import { FailureErrorPage } from 'src/pages/FailureErrorPage';
import UserGroupViewWithUsersManagement from 'src/pages/UserGroups/UserGroupViewWithUsersManagement';
import { SystemUserQueueAccess } from './pages/CaseQueues/SystemUserQueueAccess';

export const browserHistory = createBrowserHistory();

const Users = lazy(() => import('src/pages/Users/Users'));
const UserDetails = lazy(() => import('src/pages/Users/UserDetails'));
const Profiles = lazy(() => import('src/pages/Profiles/Profiles'));
const Permissions = lazy(
  () =>
    import('src/pages/ProfilePermissions/ProfileViewWithPermissionsManagement')
);
const CaseQueues = lazy(() => import('src/pages/CaseQueues/CaseQueues'));
const UserGroups = lazy(() => import('src/pages/UserGroups/UserGroups'));
const Domains = lazy(() => import('src/pages/DomainsManagement/Domains'));
const LegalReports = lazy(
  () => import('src/pages/LegalReports/AuthenticatedLegalReportsLandingPage')
);
const LandingPage = lazy(() => import('src/pages/LandingPage'));
const TRDUnifiedPage = lazy(() => import('src/pages/TRD/TRDUnifiedWorkflow'));
const BulkCaseManagement = lazy(
  () => import('src/pages/BulkCaseManagement/CaseRelatedBulkOperations')
);
const DeleteAttachment = lazy(
  () => import('src/pages/DeleteAttachment/DeleteAttachment')
);
const PageNotFoundPage = lazy(() => import('src/pages/PageNotFoundPage'));

const AppRoutes = () => {
  return (
    <React.Suspense
      fallback={
        <ContentLoader
          size={SpinnerSize.Large}
          loadingText={'Initializing'}
          fontSize={'T300'}
        />
      }
    >
      <AppErrorBoundary>
        <Switch>
          <Route path={PAGE_PATHS.HOME} exact component={LandingPage} />
          <Route path={PAGE_PATHS.DOMAINS} exact component={Domains} />
          <Route
            path={PAGE_PATHS.LEGAL_REPORTS}
            exact
            component={LegalReports}
          />
          <DomainAuthenticatedRoutes path={PAGE_PATHS.DOMAIN_VIEW}>
            <Route path={PAGE_PATHS.USERS} exact component={Users} />
            <Route
              path={PAGE_PATHS.USER_DETAILS}
              exact
              component={UserDetails}
            />
            <Route
              path={PAGE_PATHS.PROFILE_VIEW_WITH_PERMISSIONS_MANAGEMENT}
              exact
              component={Permissions}
            />
            <Route path={PAGE_PATHS.CASE_QUEUES} exact component={CaseQueues} />
            <Route
              path={PAGE_PATHS.SYSTEM_USER_QUEUE_ACCESS}
              exact
              component={SystemUserQueueAccess}
            />
            <Route path={PAGE_PATHS.PROFILES} exact component={Profiles} />
            <Route
              path={PAGE_PATHS.QUEUE_VIEW_WITH_USER_MANAGEMENT}
              exact
              component={QueueViewWithUsersManagement}
            />
            <Route
              path={PAGE_PATHS.USER_GROUP_VIEW_WITH_USER_MANAGEMENT}
              exact
              component={UserGroupViewWithUsersManagement}
            />
            <Route path={PAGE_PATHS.USER_GROUP} exact component={UserGroups} />
            <Route path={PAGE_PATHS.TRD} exact component={TRDUnifiedPage} />
            <Route
              path={PAGE_PATHS.TRD_TYPE}
              exact
              component={TRDUnifiedPage}
            />
            <Route
              path={PAGE_PATHS.TRD_REASON_FOR_CONTACT}
              exact
              component={TRDUnifiedPage}
            />
            <Route
              path={PAGE_PATHS.TRD_CONTACT_DRIVER}
              exact
              component={TRDUnifiedPage}
            />
            <Route
              path={PAGE_PATHS.BULK_CASE_MANAGEMENT}
              exact
              component={BulkCaseManagement}
            />
            <Route
              path={PAGE_PATHS.DELETE_ATTACHMENT}
              exact
              component={DeleteAttachment}
            />
          </DomainAuthenticatedRoutes>
          <Route
            path={PAGE_PATHS.FAILURE_PAGE}
            exact
            component={FailureErrorPage}
          />
          <Route path={'*'} component={PageNotFoundPage} />
        </Switch>
      </AppErrorBoundary>
    </React.Suspense>
  );
};

export default AppRoutes;
